import React, { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { get } from 'lodash'
import { DateTime } from 'luxon'
import CustomLuxonUtils from 'lib/CustomLuxonUtils'

import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import OverwriteAvailabilityDialog from './overwrite-availability-dialog'
import { aggregateByDate, aggregateByPeriod, dateRangeForMonth } from './utils'
import { useStyles, TimeSlotButton } from './styles'
import CalendarPng from 'images/calendar.png'
import { useAuth } from 'lib/auth'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import StreamSendMessageButton, {
  MaintenanceSendMessageButton,
} from 'components/stream-messaging/send-message-button'
import { gtmEvent } from 'lib/gtm'
import { toThreeLetterTimezone } from 'utils/timezones'

const SendMessage = ({ coach }) => {
  const [enabledMessagingService] = useFeatureFlags([
    flags.FLAG_MESSAGING_SERVICE,
  ])

  const sendMessageButtonProps = {
    variant: 'contained',
    color: 'primary',
    children: 'Send Message',
  }

  const SendMessageButton = (() => {
    switch (enabledMessagingService) {
      case 'stream':
        return () => (
          <StreamSendMessageButton
            userIds={[coach.externalId]}
            {...sendMessageButtonProps}
          />
        )
      case 'maintenance':
      default:
        return () => (
          <MaintenanceSendMessageButton {...sendMessageButtonProps} />
        )
    }
  })()

  return (
    <Box>
      <Box mb={3}>Send {coach.name} a message for more info.</Box>
      <SendMessageButton />
    </Box>
  )
}

const TimeSlotSelectStep = ({
  data,
  coach,
  selectedDate,
  setSelectedDate,
  currentUrl,
  refetch,
  setShowRequestTime,
}) => {
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(DateTime.local())
  const [
    isCoachExclusiveCapabilityEnabled,
    showTimezoneEnabled,
  ] = useFeatureFlags([
    flags.FLAG_FEAT_COACH_EXCLUSIVE_BOOKING_CAPABILITY,
    flags.FLAG_TIMEZONE_EVENT_BOOKING,
  ])

  const timeSlots = get(data, ['0', 'availableTimes']).map(t => t.startDateTime)
  const timeSlotsByDate = aggregateByDate(timeSlots)

  const lessonTypeId = get(data, ['0', 'id'])
  const locationId = get(data, ['0', 'availableTimes', '0', 'locationId'])
  const durationInMinutes = get(data, ['0', 'durationInMinutes'])
  const timezone = get(data, ['0', 'facilities', '0', 'timezone'])
  const formattedTimezone = toThreeLetterTimezone(timezone, selectedDate)
  const showTimezone = showTimezoneEnabled && formattedTimezone !== null

  const timeSlotsForSelectedDate =
    selectedDate != null && selectedDate.toISODate() in timeSlotsByDate
      ? timeSlotsByDate[selectedDate.toISODate()]
      : []
  const timeSlotsForSelectedDateByPeriod = aggregateByPeriod(
    timeSlotsForSelectedDate,
  )

  const classes = useStyles()
  const { user, isLoggedIn } = useAuth()
  const { idOrSlug } = useParams()

  const allowedToOverwriteSchedule =
    user?.coach?.externalId === idOrSlug ||
    user?.coach?.coachProfile?.slug === idOrSlug

  const handleMonthChange = async month => {
    const { startDateTime, endDateTime } = dateRangeForMonth(
      month,
      DateTime.local(),
    )
    await refetch({ startDateTime, endDateTime })
    setSelectedDate(startDateTime)
  }

  const RequestTime = () => {
    return (
      <Box>
        <Box mb={3}>
          Select another day or submit a request for coaching below.
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setShowRequestTime(true)
          }}
        >
          Request Coaching
        </Button>
      </Box>
    )
  }

  const handleTimeSlotClick = ts => {
    setSelectedTimeSlot(ts)
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'selected-booking-time',
    })
  }

  return (
    <>
      <Grid item container justifyContent="center" xs={12} md={5}>
        <Typography variant="h5" className={classes.heading}>
          {selectedDate.toLocaleString(DateTime.DATE_HUGE)}
        </Typography>
        <Paper elevation={0} className={classes.datePickerStyles}>
          <MuiPickersUtilsProvider utils={CustomLuxonUtils}>
            <DatePicker
              autoOk
              variant="static"
              openTo="date"
              disablePast
              disableToolbar
              shouldDisableDate={day =>
                !(day.toISODate() in timeSlotsByDate) &&
                day.toISODate() !== selectedDate.toISODate()
              }
              value={selectedDate}
              onChange={setSelectedDate}
              onMonthChange={handleMonthChange}
            />
          </MuiPickersUtilsProvider>
        </Paper>
        {allowedToOverwriteSchedule && isCoachExclusiveCapabilityEnabled && (
          <Box mt={2}>
            <Typography variant="body1">
              Don’t see the time you’re looking for? Override your availability
              by creating a new time.
            </Typography>
            <OverwriteAvailabilityDialog
              trigger={
                <Button style={{ marginLeft: '-8px' }} color="primary">
                  Choose Time
                </Button>
              }
              currentUrl={currentUrl}
              locationId={locationId}
              lessonTypeId={lessonTypeId}
              durationInMinutes={durationInMinutes}
            />
          </Box>
        )}
      </Grid>

      <Grid item xs={12} md={6} className={classes.availableTimesWrapped}>
        <Box>
          {timeSlotsForSelectedDate.length > 0 ? (
            ['AM', 'PM'].map(pd => (
              <Box key={pd} mb={2}>
                {timeSlotsForSelectedDateByPeriod[pd].length > 0 && (
                  <Typography variant="body" className={classes.periodHeading}>
                    {showTimezone ? `${pd} (${formattedTimezone})` : pd}
                  </Typography>
                )}
                <Box display="flex" flexWrap="wrap">
                  {timeSlotsForSelectedDateByPeriod[pd].map(ts => {
                    const isSelected = ts === selectedTimeSlot
                    const dateTime = DateTime.fromISO(ts).toFormat('hh:mm a')
                    return (
                      <Link
                        to={`${currentUrl}&startDateTime=${ts}`}
                        style={{ textDecoration: 'none' }}
                      >
                        <TimeSlotButton
                          key={ts}
                          onClick={() => {
                            handleTimeSlotClick(ts)
                          }}
                          variant={isSelected ? 'contained' : 'outlined'}
                          color="primary"
                        >
                          {dateTime}
                        </TimeSlotButton>
                      </Link>
                    )
                  })}
                </Box>
              </Box>
            ))
          ) : (
            <Box align="center">
              <Box mb={3}>
                <img
                  className={classes.calendarPng}
                  src={CalendarPng}
                  alt="calendar"
                />
              </Box>
              <Typography variant="h5">No Times Available</Typography>
              <Typography align="center">
                <Box mt={3}>
                  {coach.name} doesn’t have any available times on this day.
                  {!isLoggedIn && setShowRequestTime && (
                    <RequestTime user={user} />
                  )}
                  {isLoggedIn && <SendMessage coach={coach} />}
                </Box>
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>
    </>
  )
}

export default TimeSlotSelectStep
